// 一键远程登录：POST userEquipment/remote → 轮询 remoteState → 在新标签页提交远程桌面登录表单。
// 从 MachineControlPanel.vue 抽取，供机器列表行内按钮复用。
// vm：任一 Vue 组件实例（提供 $api/$url/$method/$loading/$notify/$message/$t）。
//
// 为什么先 window.open 再填内容：设备要几十秒才回连，等轮询结束再 target="_blank" 提交表单时
// 早就脱离了用户点击的手势，浏览器会把这个新窗口当弹窗**静默拦截** —— 表现就是转圈转完什么都
// 没发生（旧版界面页面里放着一个真实的 <form>，点的是真正的 submit 按钮，所以不受影响）。
// 现在在点击的当下就开一个空白标签页占位，拿到 id/code 后再把表单写进去自动提交。
const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

const REMOTE_FIELDS = remote => [
  ["c", remote.remoteId],
  ["pass", remote.remoteCode],
  // 远程桌面会话里显示的名称（原来是 "Reyeah"）
  ["dn", "VTM Vending"],
  ["lang", "0"]
];

const escapeAttr = v => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

// 往点击时开好的标签页里写一张自动提交的表单
const submitInWindow = (win, remoteId, remoteCode, url) => {
  const inputs = REMOTE_FIELDS({ remoteId, remoteCode })
    .map(([name, value]) => `<input type="hidden" name="${escapeAttr(name)}" value="${escapeAttr(value)}">`)
    .join("");
  win.document.open();
  win.document.write(
    `<!doctype html><meta charset="utf-8"><title>Remote control</title>` +
      `<body><form id="f" action="${escapeAttr(url)}" method="post">${inputs}</form>` +
      `<script>document.getElementById("f").submit();<\/script></body>`
  );
  win.document.close();
};

// 兜底：连占位标签页都被拦截时，仍按老办法在当前页动态建表单提交
const submitRemoteForm = (remoteId, remoteCode, url) => {
  const form = document.createElement("form");
  form.action = url;
  form.method = "post";
  form.target = "_blank";
  form.style.display = "none";
  REMOTE_FIELDS({ remoteId, remoteCode }).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  setTimeout(() => {
    if (form.parentNode) {
      form.parentNode.removeChild(form);
    }
  }, 1000);
};

export const remoteLogin = (vm, machine) => {
  let countError = 0;
  // 取消条件：组件已销毁，或在此期间又发起了一次远程登录（vm.__remoteSeq 变化）。
  // 命中后停止轮询、不提交远程桌面表单、不弹已失效的提示，避免为已离开的设备打开会话。
  const seq = (vm.__remoteSeq = (vm.__remoteSeq || 0) + 1);
  const cancelled = () => vm._isDestroyed || vm.__remoteSeq !== seq;

  // 趁点击手势还在，先把标签页开出来（内容稍后写入）。被拦截时为 null，走兜底提交。
  let win = null;
  try {
    win = window.open("", "_blank");
    if (win && win.document) {
      win.document.write("<!doctype html><meta charset=\"utf-8\"><title>Remote control</title><body style=\"font:14px system-ui;padding:24px\">Connecting to the machine…</body>");
    }
  } catch (e) {
    win = null;
  }
  const closeWin = () => {
    try {
      if (win && !win.closed) {
        win.close();
      }
    } catch (e) {
      /* ignore */
    }
    win = null;
  };
  const openRemote = data => {
    if (win && !win.closed) {
      submitInWindow(win, data.remoteId, data.remoteCode, data.gotoHttpUrl);
      win = null;
    } else {
      submitRemoteForm(data.remoteId, data.remoteCode, data.gotoHttpUrl);
    }
  };

  const loading = vm.$loading({
    lock: true,
    text: vm.$t("common.ljload"),
    spinner: "el-icon-loading",
    background: "rgba(0,0,0,0.7)"
  });
  const closeLoading = () => {
    try {
      loading.close();
    } catch (e) {
      /* ignore */
    }
    closeWin();
  };

  const poll = async () => {
    await delay(3000);
    if (cancelled()) {
      closeLoading();
      return;
    }
    countError++;
    if (countError > 20) {
      closeLoading();
      vm.$message.warning(vm.$t("user.MachineMaps.588934-10"));
      return;
    }
    vm.$api
      .request(vm.$url.UserMachine.getcontrol, vm.$method.get, { equipmentNo: machine.equipmentNo })
      .then(res => {
        if (cancelled()) {
          closeLoading();
          return;
        }
        if (res.code === 0 && res.data && res.data.remoteState === 2) {
          // 先把会话写进已开好的标签页，再关 loading —— closeLoading 会顺手关掉占位标签页
          openRemote(res.data);
          closeLoading();
        } else if (res.code === 0 && res.data && res.data.remoteState === -1) {
          closeLoading();
          vm.$notify({ title: vm.$t("common.tip"), message: vm.$t("common.ljfail"), type: "error" });
        } else if (res.code === 0) {
          setTimeout(poll, 1000);
        } else {
          closeLoading();
          vm.$notify({ title: vm.$t("common.tip"), message: res.msg || vm.$t("machinesHub.commandFailed"), type: "warning" });
        }
      })
      .catch(() => {
        if (cancelled()) {
          closeLoading();
          return;
        }
        closeLoading();
        vm.$notify({ title: vm.$t("common.tip"), message: vm.$t("common.ljfail"), type: "error" });
      });
  };

  vm.$api
    .request(vm.$url.UserMachine.control, vm.$method.post, { equipmentNo: machine.equipmentNo })
    .then(res => {
      if (cancelled()) {
        closeLoading();
        return;
      }
      if (res.code === 0) {
        poll();
      } else {
        closeLoading();
        vm.$notify({ title: vm.$t("common.tip"), message: res.msg || vm.$t("machinesHub.commandFailed"), type: "warning" });
      }
    })
    .catch(() => {
      closeLoading();
      if (!cancelled()) {
        vm.$notify({ title: vm.$t("common.tip"), message: "request error", type: "warning" });
      }
    });
};

// 取消某组件上进行中的远程登录轮询（在 beforeDestroy 中调用）
export const cancelRemoteLogin = vm => {
  if (vm) {
    vm.__remoteSeq = (vm.__remoteSeq || 0) + 1;
  }
};

export default { remoteLogin, cancelRemoteLogin };
